import { SafeAny } from '@models/common';
import { Classification, TaxRate } from '@models/product';
import { Company, UserListRequest } from '@models/settings';
import { downloadPdf } from '@services/pdf';
import {
  createClassification,
  createSubscription,
  createTaxRate,
  getAdminConfig,
  getCompany,
  getCompanyInformation,
  getCurrencySettings,
  getListClassification,
  getListTaxRate,
  getSubscriptionConfirm,
  getSubscriptions,
  getUserInfo,
  getUserList,
  redirectStripeCustomerPostal,
  updateAdminConfigFeatures,
  updateAdminConfigSetting,
  updateClassification,
  updatePaypalMail,
  updateTaxRate,
  updateTenantCurrency,
  updateUserInfo
} from '@services/settings';
import { EStorageKeys, storageGet } from '@services/storage';
import { isEmpty, omit } from 'lodash-es';
import { useMutation, useQuery, useQueryClient, UseQueryResult } from 'react-query';

export const CLASSIFICATION_LIST = 'CLASSIFICATION_LIST';
export const TAX_RATE_LIST = 'TAX_RATE_LIST';
export const TENANT_CURRENCY = 'TENANT_CURRENCY';
export const CURRENCY_SETTING = 'CURRENCY_SETTING';
export const ADMIN_CONFIG = 'ADMIN_CONFIG';
export const COMPANY_SUBSCRIPTION = 'COMPANY_SUBSCRIPTION';
export const SUBSCRIPTIONS = 'SUBSCRIPTIONS';
export const SUBSCRIPTION_CONFIRM = 'SUBSCRIPTION_CONFIRM';
export const SUBSCRIPTION_ENABLED = 'SUBSCRIPTION_ENABLED';
export const COMPANY = 'COMPANY';
export const COMPANY_STATUS = 'COMPANY_STATUS';
export const USER_LIST = 'USER_LIST';
export const TENANT_LIST = 'TENANT_LIST';
export const TAPFILIATE = 'TAPFILIATE';
export const COMPANY_INFORMATION = 'COMPANY_INFORMATION';
export const USER_INFO = 'USER_INFO';

export const useClassificationList = () => useQuery([CLASSIFICATION_LIST], () => getListClassification());

export const useUpsertClassification = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (item: Classification) =>
      item.id === 'NEW_ID' ? createClassification(omit(item, ['id'])) : updateClassification(item),
    {
      onSuccess: () => queryClient.invalidateQueries([CLASSIFICATION_LIST])
    }
  );
};

export const useTaxRateList = () => useQuery([TAX_RATE_LIST], () => getListTaxRate());

export const useUpsertTaxRate = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (item: TaxRate) => (item.id === 'NEW_ID' ? createTaxRate(omit(item, ['id'])) : updateTaxRate(item)),
    {
      onSuccess: () => queryClient.invalidateQueries([TAX_RATE_LIST])
    }
  );
};

export const useTenantCurrency = () => {
  const { data } = useCurrencyStorage();
  const queryClient = useQueryClient();
  let currency = queryClient.getQueryData<SafeAny>([TENANT_CURRENCY]) || {};
  if (isEmpty(currency) && data) {
    currency = data;
  }
  return {
    currencyName: currency['Currency.Name'] || 'Euro',
    currencySymbol: currency['Currency.Symbol'] || '€'
  };
};

export const useCurrencyStorage = () =>
  useQuery(['GET_CURRENCY_STORAGE'], () => {
    return storageGet(EStorageKeys.CURRENCY).then((data) => {
      return data;
    });
  });

export const useCurrencySettings = () => useQuery([CURRENCY_SETTING], getCurrencySettings);

export const useUpdateTenantCurrency = () => {
  const queryClient = useQueryClient();

  return useMutation(updateTenantCurrency, {
    onSuccess: () => {
      queryClient.invalidateQueries([ADMIN_CONFIG]);
    }
  });
};

export const useAdminConfig = (enabled?: boolean) =>
  useQuery([ADMIN_CONFIG], () => getAdminConfig(), {
    staleTime: Infinity,
    enabled
  });

export const useUpdateEmail = () => {
  const clientQuery = useQueryClient();
  return useMutation((paypalAddress: string) => updatePaypalMail(paypalAddress), {
    onSuccess: () => clientQuery.invalidateQueries([TAPFILIATE])
  });
};

export const useDownloadPdf = () => useMutation(downloadPdf);

export const useGetSubscriptions = () => useQuery([SUBSCRIPTIONS], () => getSubscriptions());

export const useCreateSubscription = () => {
  return useMutation(createSubscription);
};

export const useRedirectStripeCustomerPostal = () => useMutation(redirectStripeCustomerPostal);

export const useGetCompanyInformation = (enabled?: boolean) =>
  useQuery([COMPANY_INFORMATION], () => getCompanyInformation(), { enabled });

export const useGetCompany = () => {
  return useQuery([COMPANY], () => getCompany(), {
    staleTime: Infinity
  }) as UseQueryResult<SafeAny, Company>;
};

export const useGetSubscriptionConfirm = () => {
  const clientQuery = useQueryClient();
  return useMutation(getSubscriptionConfirm, {
    onSuccess: () => {
      clientQuery.invalidateQueries([COMPANY_SUBSCRIPTION]);
      clientQuery.invalidateQueries([COMPANY_INFORMATION]);
    }
  });
};

export const useGetUserList = (params: Partial<UserListRequest>) =>
  useQuery([USER_LIST, params], () => getUserList(params));

export const useUpdateAdminConfigFeatures = () => {
  const clientQuery = useQueryClient();
  return useMutation(updateAdminConfigFeatures, {
    onSuccess: () => {
      clientQuery.invalidateQueries([ADMIN_CONFIG]);
    }
  });
};

export const useUpdateAdminConfigSetting = () => {
  const clientQuery = useQueryClient();
  return useMutation(updateAdminConfigSetting, {
    onSuccess: () => {
      clientQuery.invalidateQueries([ADMIN_CONFIG]);
    }
  });
};

export const useGetUserInfo = (enabled?: boolean) => useQuery([USER_INFO], () => getUserInfo(), { enabled });

export const useUpdateUserInfo = () => {
  const clientQuery = useQueryClient();
  return useMutation(updateUserInfo, {
    onSuccess: () => {
      clientQuery.invalidateQueries([USER_INFO]);
    }
  });
};
